import { useState } from "react";
import { UserPlus, Users, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { formatCurrency, formatSeatRoleLabel } from "../../lib/subscriptionBilling";

const SEAT_ROLES = {
  NP: "nurse_practitioner",
  BACK_OFFICE: "back_office",
};

export default function SeatManagementUI({
  seats = [],
  maxSeats = 2,
  seatPricePerMonth = 15,
  onInviteSeat,
  onRemoveSeat,
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(SEAT_ROLES.NP);
  const [inviteError, setInviteError] = useState("");
  const [seatPendingRemove, setSeatPendingRemove] = useState(null);

  const atMaxSeats = seats.length >= maxSeats;
  const trimmedEmail = email.trim();
  const showCostPreview = trimmedEmail.length > 0 && !atMaxSeats;

  const validateInvite = () => {
    if (!trimmedEmail) return "Email is required.";
    if (!trimmedEmail.includes("@")) return "Enter a valid email address.";
    if (seats.some((s) => s.email.toLowerCase() === trimmedEmail.toLowerCase())) {
      return "This email already has a seat on your account.";
    }
    return "";
  };

  const handleInvite = () => {
    const error = validateInvite();
    if (error) {
      setInviteError(error);
      return;
    }
    onInviteSeat(trimmedEmail, role);
    setEmail("");
    setRole(SEAT_ROLES.NP);
    setInviteError("");
  };

  const handleConfirmRemove = () => {
    if (seatPendingRemove) {
      onRemoveSeat(seatPendingRemove.id);
      setSeatPendingRemove(null);
    }
  };

  return (
    <>
      <Card className="shadow-md border border-gray-100" data-tour="subscription-seats">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">Supporting User Seats</CardTitle>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Add up to {maxSeats} supporting users (Nurse Practitioner or Back Office Staff).
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-3 text-sm text-gray-700">
            <span className="font-medium text-gray-900">1 Doctor</span> — included with your
            subscription
          </div>

          {!atMaxSeats ? (
            <div className="space-y-4 rounded-lg border border-gray-200 p-4 bg-gray-50/50">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <UserPlus className="w-4 h-4 text-blue-600" />
                Invite supporting user
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="seat-role">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger id="seat-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SEAT_ROLES.NP}>Nurse Practitioner</SelectItem>
                      <SelectItem value={SEAT_ROLES.BACK_OFFICE}>
                        Back Office Staff
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="seat-email">Email address</Label>
                  <Input
                    id="seat-email"
                    type="email"
                    placeholder="colleague@clinic.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setInviteError("");
                    }}
                  />
                </div>
              </div>

              {showCostPreview && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  <span className="font-medium">Cost preview:</span> +{" "}
                  {formatCurrency(seatPricePerMonth)} / month (billed with next cycle)
                </div>
              )}

              {inviteError && <p className="text-sm text-red-600">{inviteError}</p>}

              <Button
                type="button"
                onClick={handleInvite}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Confirm invite
              </Button>
            </div>
          ) : (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              Maximum {maxSeats} supporting users on your account. Remove a seat to invite
              someone else.
            </p>
          )}

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Active seats ({seats.length}/{maxSeats})
            </h4>

            {seats.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center border border-dashed border-gray-200 rounded-lg">
                No supporting users added yet.
              </p>
            ) : (
              <div className="space-y-2">
                {seats.map((seat) => (
                  <div
                    key={seat.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg bg-white"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{seat.email}</p>
                      <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                        {formatSeatRoleLabel(seat.role)}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 shrink-0"
                      onClick={() => setSeatPendingRemove(seat)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={Boolean(seatPendingRemove)}
        onOpenChange={(open) => {
          if (!open) setSeatPendingRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove supporting user?</AlertDialogTitle>
            <AlertDialogDescription>
              {seatPendingRemove
                ? `${seatPendingRemove.email} will lose access. Billing will adjust on your next billing cycle.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type="button"
              onClick={handleConfirmRemove}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Remove user
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
